/**
 * VersionHistoryModal
 *
 * Simple modal for browsing saved version snapshots and restoring previous versions.
 * Reads directly from editorStore.project.versions[] (populated on project load).
 * Does NOT use the versioning store or versioning API routes.
 */

import React, { useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useEditorStore } from "@/stores/editor/editor-store";
import { ProjectsApi } from "@/services/api/projects";
import { toast } from "sonner";
import {
  History,
  Save,
  RotateCcw,
  Clock,
  Loader2,
  ChevronRight,
  Camera,
} from "lucide-react";

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSnapshot?: (name?: string) => Promise<boolean | void>;
  onRestoreComplete?: () => void;
}

export const VersioningPanel: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  onSaveSnapshot,
  onRestoreComplete,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const project = useEditorStore((s) => s.project);
  const setProject = useEditorStore((s) => s.setProject);

  const [snapshotName, setSnapshotName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [confirmVersion, setConfirmVersion] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("snapshots");

  const allVersions = useMemo(() => {
    const v = project?.versions ?? [];
    return [...v].sort(
      (a: any, b: any) =>
        (b.version ?? 0) - (a.version ?? 0) ||
        new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
    );
  }, [project?.versions]);

  const isSnapshot = (version: any): boolean =>
    version.tags?.includes("snapshot");

  const snapshots = useMemo(
    () => allVersions.filter(isSnapshot),
    [allVersions],
  );

  const saves = useMemo(
    () => allVersions.filter((v: any) => !isSnapshot(v)),
    [allVersions],
  );

  const currentVersion = project?.currentVersion ?? 0;

  const getVersionRowKey = (version: any) =>
    version.id ??
    version._id ??
    `${version.version}-${new Date(version.createdAt ?? 0).getTime()}-${version.name ?? "unnamed"}`;

  const currentVersionKey = useMemo(() => {
    const currentRow = allVersions.find((version: any) => version.version === currentVersion);
    return currentRow ? getVersionRowKey(currentRow) : null;
  }, [allVersions, currentVersion]);

  const handleSaveSnapshot = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (onSaveSnapshot) {
        await onSaveSnapshot(snapshotName.trim() || undefined);
      }
      setSnapshotName("");
      toast.success("Snapshot saved");
    } catch {
      toast.error("Failed to save snapshot");
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, onSaveSnapshot, snapshotName]);

  const handleConfirmRestore = useCallback(async () => {
    if (!confirmVersion || !project || !projectId) return;
    setIsRestoring(true);
    try {
      if (onSaveSnapshot) {
        try {
          await onSaveSnapshot();
        } catch {
          // Non-fatal — proceed with restore even if save fails
        }
      }

      await ProjectsApi.restoreVersion(projectId, String(confirmVersion));
      const refreshedProject = await ProjectsApi.getProject(projectId);

      if (!refreshedProject.success || !refreshedProject.data) {
        throw new Error(
          refreshedProject.message || "Failed to reload restored project",
        );
      }

      setProject(refreshedProject.data);

      onRestoreComplete?.();

      toast.success(`Restored to version ${confirmVersion}`);
      onClose();
    } catch (err) {
      toast.error(
        `Failed to restore: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setIsRestoring(false);
      setConfirmVersion(null);
    }
  }, [confirmVersion, project, projectId, onSaveSnapshot, setProject, onClose, onRestoreComplete]);

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const confirmTarget = confirmVersion !== null
    ? allVersions.find((v: any) => v.version === confirmVersion)
    : null;

  const renderVersionRow = (version: any) => {
    const isCurrent = getVersionRowKey(version) === currentVersionKey;
    return (
      <div
        key={getVersionRowKey(version)}
        className={`group flex items-center gap-3 px-4 py-2.5 transition-colors ${
          isCurrent ? "bg-primary/5" : "hover:bg-accent"
        }`}
      >
        {/* Version number badge */}
        <div
          className={`flex items-center justify-center w-7 h-7 rounded-full shrink-0 text-[11px] font-semibold tabular-nums ${
            isCurrent ? "text-primary-foreground bg-primary" : "text-muted-foreground bg-muted"
          }`}
        >
          {version.version}
        </div>

        {/* Name + timestamp */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[13px] font-medium truncate"
              style={{ color: 'hsl(var(--foreground))' }}
            >
              {version.name || `Save ${version.version}`}
            </span>
            {isCurrent && (
              <Badge
                variant="outline"
                className="text-[10px] shrink-0 border-emerald-300 text-emerald-700 px-1.5 py-0"
              >
                Current
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3 shrink-0" style={{ color: 'hsl(var(--muted-foreground))' }} />
            <span className="text-[11px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {formatDate(version.createdAt)}
            </span>
          </div>
        </div>

        {/* Restore button */}
        {!isCurrent && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs shrink-0 text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => setConfirmVersion(version.version)}
            disabled={isRestoring}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Restore
          </Button>
        )}
      </div>
    );
  };

  const renderEmptyState = (tab: string) => (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      {tab === "snapshots" ? (
        <Camera className="w-9 h-9 mb-2.5" style={{ color: 'hsl(var(--muted-foreground))' }} />
      ) : (
        <History className="w-9 h-9 mb-2.5" style={{ color: 'hsl(var(--muted-foreground))' }} />
      )}
      <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
        {tab === "snapshots" ? "No snapshots yet" : "No auto saves yet"}
      </p>
      <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
        {tab === "snapshots"
          ? "Save a named snapshot to create your first one"
          : "Saves are created automatically when you save your project"}
      </p>
    </div>
  );

  const renderVersionList = (versions: any[]) => (
    <div className="overflow-y-auto" style={{ maxHeight: "380px" }}>
      {versions.length === 0
        ? renderEmptyState(activeTab)
        : (
          <div className="divide-y divide-gray-100">
            {versions.map(renderVersionRow)}
          </div>
        )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="sm:max-w-2xl p-0 gap-0">
        {/* Header */}
        <div
          className="flex items-center justify-between pl-5 pr-12 py-3.5 border-b"
          style={{
            backgroundColor: 'hsl(var(--primary) / 0.03)',
            borderColor: 'hsl(var(--primary) / 0.19)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-5 rounded-full"
              style={{ backgroundColor: 'hsl(var(--primary))' }}
            />
            <nav className="flex items-center gap-1" aria-label="Breadcrumb">
              <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                {project?.name || "Project"}
              </span>
              <ChevronRight className="w-3.5 h-3.5" style={{ color: 'hsl(var(--muted-foreground))' }} />
              <span
                className="flex items-center gap-1.5 text-sm font-medium"
                style={{ color: 'hsl(var(--primary))' }}
              >
                <History className="w-3.5 h-3.5" />
                Version History
              </span>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-xs font-medium">
              v{currentVersion}
            </Badge>
            <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {allVersions.length} version{allVersions.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {confirmVersion !== null ? (
          /* ── Inline Restore Confirmation ── */
          <div className="flex flex-col items-center justify-center px-8 py-12 text-center">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-full mb-4"
              style={{ backgroundColor: 'hsl(var(--primary) / 0.08)' }}
            >
              <RotateCcw className="w-6 h-6" style={{ color: 'hsl(var(--primary))' }} />
            </div>
            <h3 className="text-base font-semibold mb-2" style={{ color: 'hsl(var(--foreground))' }}>
              Restore to version {confirmVersion}?
            </h3>
            <p className="text-sm mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {confirmTarget?.name || `Save ${confirmVersion}`}
              {confirmTarget?.createdAt && (
                <span> &middot; {formatDate(confirmTarget.createdAt)}</span>
              )}
            </p>
            <p className="text-xs max-w-sm mb-6" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Your current state will be saved first, then the editor will load
              this version's snapshot. You can always restore back later.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs px-4"
                onClick={() => setConfirmVersion(null)}
                disabled={isRestoring}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs px-4"
                onClick={handleConfirmRestore}
                disabled={isRestoring}
                style={{ backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
              >
                {isRestoring ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                )}
                Restore
              </Button>
            </div>
          </div>
        ) : (
          /* ── Tabbed Version List ── */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
            {/* Tab bar — matches DataDiscoveryModal pattern */}
            <div className="border-b border-border px-4 pt-3 pb-2 bg-muted">
              <TabsList className="grid w-full grid-cols-2 h-9">
                <TabsTrigger value="snapshots" className="flex items-center gap-1.5 text-xs">
                  <Camera className="h-3.5 w-3.5" />
                  Snapshots
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0 tabular-nums">
                    {snapshots.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="saves" className="flex items-center gap-1.5 text-xs">
                  <Save className="h-3.5 w-3.5" />
                  Auto Saves
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0 tabular-nums">
                    {saves.length}
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Snapshots Tab */}
            <TabsContent value="snapshots" className="mt-0">
              {/* Save Snapshot input */}
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Snapshot name (optional)"
                    value={snapshotName}
                    onChange={(e) => setSnapshotName(e.target.value)}
                    className="flex-1 h-8 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveSnapshot();
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveSnapshot}
                    disabled={isSaving}
                    className="h-8 text-xs shrink-0"
                    style={{ backgroundColor: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
                  >
                    {isSaving ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Camera className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Save Snapshot
                  </Button>
                </div>
              </div>
              {renderVersionList(snapshots)}
            </TabsContent>

            {/* Auto Saves Tab */}
            <TabsContent value="saves" className="mt-0">
              {renderVersionList(saves)}
            </TabsContent>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/50">
              <p className="text-[11px]" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Max {project?.settings?.maxVersions || 50} auto saves kept. Snapshots are never pruned.
              </p>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-7 text-xs">
                Close
              </Button>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersioningPanel;
